import { useMemo } from 'react';
import {
  getOverlayIndicators,
  getSubplotIndicators
} from '../utils/chart.util';
import { DataFeedResponse } from '@server/data-feed/schemas/output.schema';
import { IndicatorConfig, IndicatorConfigs } from '../types/indicators.types';
import { IndicatorType } from '@shared/types';
import { convertToTime } from '../utils/time-conversion.utils';

const subplotIndicatorKeys = getSubplotIndicators();
const overlayIndicatorKeys = getOverlayIndicators();

type RawDataItem = { time: string };
type IndicatorLike = {
  type: IndicatorType;
  period?: number;
};

// Generic parseData function that rounds the timestamp
export function parseData<T extends RawDataItem, U>(
  data: T[],
  transformItem: (item: T, time: /*Time*/ any) => U
): U[] {
  return data.map((item) => {
    return transformItem(item, convertToTime(item.time));
  });
}

export const parseCandlestickData = (
  timeframeFeed: NonNullable<DataFeedResponse['timeframes']>[number]
): NonNullable<DataFeedResponse['timeframes']>[number]['candlesticks'] =>
  parseData(timeframeFeed.candlesticks, (item, time) => ({
    time,
    close: item.close,
    open: item.open,
    low: item.low,
    high: item.high,
    volume: item.volume
  }));

const toIndicatorConfig = (indicator: IndicatorLike): IndicatorConfig => {
  if (indicator.type === 'MA') {
    return {
      type: indicator.type,
      period: indicator.period
    };
  }

  return { type: indicator.type };
};

export const useIndicatorConfigs = (
  data: DataFeedResponse | undefined,
  timeframeIndex: number,
  fallbackIndicators?: IndicatorLike[]
): IndicatorConfigs => {
  return useMemo(() => {
    // Rendering toggles are UI-state; prefer current form indicators over feed payload.
    const indicators =
      fallbackIndicators ?? data?.timeframes?.[timeframeIndex]?.indicators;
    if (!indicators) return { subplot: [], overlay: [] };

    const subplot: IndicatorConfig[] = [];
    const overlay: IndicatorConfig[] = [];

    indicators.forEach((indicator) => {
      const configs = toIndicatorConfig(indicator);

      if (subplotIndicatorKeys.includes(indicator.type)) {
        subplot.push(configs);
      }
      if (overlayIndicatorKeys.includes(indicator.type)) {
        overlay.push(configs);
      }
    });

    return {
      subplot,
      overlay
    };
  }, [data, fallbackIndicators, timeframeIndex]);
};
